import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { EventItemsQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { useState } from 'react'
import { EventItemFilters } from '@/types'
import { Avatar } from '../base/avatar'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { EditDialog } from './edit_dialog'
import { NewDialog } from './new_dialog'

interface Props extends TableProps {
  eventId: string
  createable?: boolean
  updateable?: boolean
}

export const Table = ({ pageSize = 10, eventId, createable = false, updateable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<EventItemsQuery>(
    Query,
    { variables: { eventId, first: pageSize } }
  )
  const {
    pageInfo,
    openNew,
    setOpenNew,
    openEdit,
    setOpenEdit
  } = useTable({ pageInfo: data?.events.nodes[0].eventItems.pageInfo })
  const [eventItemId, setEventItemId] = useState<string>()

  const editEventItem = (id: string) => {
    if (!updateable) return

    setEventItemId(id)
    setOpenEdit(true)
  }

  if (error) return <Error message={error.message} />
  if (loading || !data) return <Loading />

  const stages = [...new Set(data.events.nodes[0].eventItems.nodes.map(s => s.eventStage).filter(n => n != undefined))]

  return (
    <>
      <BaseTable<EventItemFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        headings={[{ name: 'Name' }, { name: 'Stage' }, { name: 'Quantity' }]}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.events.nodes[0].eventItems.nodes = dedupe([
              ...previousData.events.nodes[0].eventItems.nodes,
              ...fetchMoreResult.events.nodes[0].eventItems.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, itemNameCont: value })),
            type: 'text',
            placeholder: 'What is it?'
          },
          {
            index: 1,
            setValue: (set, value) => set(f => ({ ...f, eventStageIdEq: value })),
            type: 'select',
            options: stages.map(s => ({ value: s.id, display: s.name }))
          }
        ]}
        rows={
          [...data.events.nodes[0].eventItems.nodes]
            .sort((a, b) => ('' + a.item.name).localeCompare(b.item.name))
            .map(eventItem => (
              {
                id: eventItem.id,
                columns: [
                  <div className="flex flex-row items-center gap-4">
                    <span className="w-16">
                      <Avatar
                        className="size-16 bg-white *:size-16 *:object-contain"
                        src={eventItem.item.images[0]?.url || data.companies.nodes[0]?.placeholderLogo?.url}
                        square
                      />
                    </span>
                    { eventItem.item.name }
                  </div>,
                  eventItem.eventStage?.name,
                  eventItem.quantity
                ]
              }
            ))
        }
        onRowClick={editEventItem}
      />
      { eventItemId && <EditDialog eventId={eventId} id={eventItemId} open={openEdit} setOpen={setOpenEdit} /> }
      <NewDialog eventId={eventId} open={openNew} setOpen={setOpenNew} />
    </>
  )
}
